import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { logger } from "@/lib/logger";

const STORAGE_KEY = "feaststar.cookie.consent.v1";

/**
 * Minimal DSGVO-compliant cookie consent banner.
 * Two clear choices (Akzeptieren / Ablehnen) + link to Datenschutz.
 * Persists choice in localStorage; doesn't re-appear once decided.
 *
 * The Notify-Modal auto-popup waits for this banner to be decided
 * (see NotifyContext).
 */
export default function CookieConsentBanner() {
  const { t } = useLanguage();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    let decided = null;
    try {
      decided = window.localStorage.getItem(STORAGE_KEY);
    } catch (err) {
      logger.warn("[cookie-banner] localStorage read failed:", err);
    }
    if (!decided) {
      // Slight delay so banner doesn't compete with hero on first paint
      const t1 = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t1);
    }
  }, []);

  // Re-open on demand (Footer "Cookie-Einstellungen" → widerruft die Einwilligung
  // gem. Art. 7 Abs. 3 DSGVO). Deletes the stored decision so the banner
  // behaves like on first visit.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onReopen = () => {
      try {
        window.localStorage.removeItem(STORAGE_KEY);
      } catch (err) {
        logger.warn("[cookie-banner] localStorage clear failed:", err);
      }
      setVisible(true);
    };
    window.addEventListener("feaststar:cookie-reopen", onReopen);
    return () => window.removeEventListener("feaststar:cookie-reopen", onReopen);
  }, []);

  const decide = (choice) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        choice, // "accept" | "decline"
        at: new Date().toISOString(),
      }));
      // Broadcast event so other components (NotifyContext) can react
      window.dispatchEvent(new CustomEvent("feaststar:cookie-consent", { detail: { choice } }));
    } catch (err) {
      logger.warn("[cookie-banner] persist choice failed:", err);
    }
    // Google Consent Mode v2 — grant / deny analytics_storage based on choice.
    // gtag was initialised in index.html with 'denied' defaults, so the
    // 'update' call below is what actually enables GA4 collection.
    try {
      if (typeof window.gtag === "function") {
        window.gtag("consent", "update", {
          analytics_storage: choice === "accept" ? "granted" : "denied",
        });
        // On first-visit Accept, gtag('config',…) already ran while consent
        // was denied, so its automatic initial page_view was suppressed.
        // Fire it manually now so the very first visit is not lost.
        if (choice === "accept") {
          window.gtag("event", "page_view", {
            page_location: window.location.href,
            page_title: document.title,
            page_path: window.location.pathname + window.location.search,
            send_to: "G-142KLGVXS2",
          });
        }
      }
    } catch (err) {
      logger.warn("[cookie-banner] gtag consent update failed:", err);
    }
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="cookie-banner"
          data-testid="cookie-banner"
          role="dialog"
          aria-label={t("cookie.title")}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="cookie-card">
            <div className="cookie-header">
              <div className="cookie-icon" aria-hidden="true">
                <Cookie size={18} strokeWidth={1.75} />
              </div>
              <div className="cookie-text">
                <h3 className="cookie-title">{t("cookie.title")}</h3>
                <p className="cookie-body">
                  {t("cookie.body")}{" "}
                  <Link to="/datenschutz" className="cookie-link" data-testid="cookie-privacy-link">
                    {t("cookie.learnMore")}
                  </Link>.
                </p>
              </div>
            </div>
            <div className="cookie-actions">
              <button
                type="button"
                onClick={() => decide("decline")}
                data-testid="cookie-decline"
                className="cookie-btn cookie-btn-ghost"
              >
                {t("cookie.decline")}
              </button>
              <button
                type="button"
                onClick={() => decide("accept")}
                data-testid="cookie-accept"
                className="cookie-btn cookie-btn-primary"
              >
                {t("cookie.accept")}
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
